import { useState, useRef, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { Play, Clock, Eye, ChevronLeft, ChevronRight, ChevronDown } from "lucide-react";

// ── Video Modal ────────────────────────────────────────
function VideoModal({ video, onClose }) {
  if (!video) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer"
      onClick={onClose}
      data-testid="video-modal-overlay"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 30 }}
        transition={{ type: "spring", stiffness: 260, damping: 25 }}
        className="w-full max-w-5xl rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-video bg-black">
          <iframe
            src={`${video.embedUrl}?autoplay=1&rel=0`}
            title={video.title}
            className="w-full h-full"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
          />
        </div>
        <div className="p-4 md:p-5 bg-[#0A0A0C]">
          <h3 className="font-heading text-base md:text-lg font-semibold text-white">{video.title}</h3>
          <div className="mt-2 flex items-center gap-4 text-xs text-zinc-500">
            <span className="flex items-center gap-1"><Eye size={11} /> {video.views}</span>
            {video.duration && <span className="flex items-center gap-1"><Clock size={11} /> {video.duration}</span>}
            {video.category && <span className="text-[#E63946] font-mono uppercase text-[10px]">{video.category}</span>}
          </div>
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Shorts Modal (Instagram embed) ─────────────────────
function ShortsModal({ video, onClose }) {
  if (!video) return null;
  return (
    <motion.div
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
      className="fixed inset-0 z-[100] flex items-center justify-center bg-black/90 backdrop-blur-md p-4 cursor-pointer"
      onClick={onClose}
      data-testid="shorts-modal-overlay"
    >
      <motion.div
        initial={{ scale: 0.85, opacity: 0, y: 30 }}
        animate={{ scale: 1, opacity: 1, y: 0 }}
        exit={{ scale: 0.85, opacity: 0, y: 30 }}
        transition={{ type: "spring", stiffness: 260, damping: 25 }}
        className="w-full max-w-sm rounded-2xl overflow-hidden border border-white/10 shadow-2xl shadow-black/50 cursor-default"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="relative aspect-[9/16] bg-black">
          <iframe
            src={video.embedUrl}
            title={video.title}
            className="w-full h-full border-0"
            allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
            allowFullScreen
            scrolling="no"
          />
        </div>
      </motion.div>
    </motion.div>
  );
}

// ── Long Video Card (hover = muted YouTube preview) ────
function LongVideoCard({ video, onClick, index }) {
  const [hovered, setHovered] = useState(false);
  const [showPreview, setShowPreview] = useState(false);
  const hoverTimer = useRef(null);

  const handleMouseEnter = () => {
    setHovered(true);
    hoverTimer.current = setTimeout(() => setShowPreview(true), 600);
  };

  const handleMouseLeave = () => {
    setHovered(false);
    setShowPreview(false);
    if (hoverTimer.current) clearTimeout(hoverTimer.current);
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, delay: index * 0.06 }}
      data-testid={`featured-video-${video.id}`}
      className="group relative rounded-xl overflow-hidden cursor-pointer"
      onMouseEnter={handleMouseEnter}
      onMouseLeave={handleMouseLeave}
      onClick={() => onClick(video)}
    >
      <div className="relative aspect-video overflow-hidden rounded-xl bg-[#0F0F11]">
        <img
          src={video.thumbnail}
          alt={video.title}
          className={`w-full h-full object-cover transition-transform duration-700 ${hovered ? "scale-110" : "scale-100"}`}
          loading="lazy"
        />

        {showPreview && (
          <div className="absolute inset-0 z-[2]">
            <iframe
              src={`${video.embedUrl}?autoplay=1&mute=1&controls=0&modestbranding=1&rel=0&showinfo=0&loop=1`}
              title={`Preview: ${video.title}`}
              className="w-full h-full border-0"
              allow="autoplay; encrypted-media"
            />
          </div>
        )}

        <div className={`absolute inset-0 z-[3] transition-all duration-500 pointer-events-none ${
          hovered ? "bg-gradient-to-t from-black/90 via-black/20 to-transparent" : "bg-gradient-to-t from-black/60 via-transparent to-transparent"
        }`} />

        <div className={`absolute inset-0 z-[4] flex items-center justify-center transition-all duration-300 pointer-events-none ${hovered ? "opacity-100" : "opacity-0"}`}>
          <div className="w-14 h-14 md:w-16 md:h-16 rounded-full bg-[#E63946] flex items-center justify-center shadow-lg shadow-[#E63946]/30">
            <Play size={22} className="text-white ml-1" fill="white" />
          </div>
        </div>

        {video.duration && (
          <div className="absolute bottom-2 right-2 md:bottom-3 md:right-3 z-[4] px-2 py-0.5 md:px-2.5 md:py-1 bg-black/80 backdrop-blur-sm text-white text-[10px] md:text-[11px] font-mono rounded-md flex items-center gap-1">
            <Clock size={10} />{video.duration}
          </div>
        )}

        <div className={`absolute bottom-0 left-0 right-0 p-3 md:p-5 z-[4] transition-all duration-500 pointer-events-none ${
          hovered ? "translate-y-0 opacity-100" : "translate-y-4 opacity-0"
        }`}>
          <h3 className="font-heading text-sm md:text-lg font-semibold text-white leading-tight drop-shadow-lg">{video.title}</h3>
          <div className="mt-1 md:mt-2 flex items-center gap-3">
            {video.category && <span className="text-[#E63946] font-mono text-[9px] md:text-[10px] uppercase tracking-wider">{video.category}</span>}
            {video.views && <span className="flex items-center gap-1 text-zinc-400 text-[10px] md:text-[11px]"><Eye size={10} /> {video.views}</span>}
          </div>
        </div>
      </div>

      {hovered && <div className="absolute -inset-[1px] rounded-xl border border-[#E63946]/30 pointer-events-none z-[5]" />}
    </motion.div>
  );
}

// ── Short Video Card (thumbnail only) ──────────────────
function ShortVideoCard({ video, onClick, index }) {
  const [hovered, setHovered] = useState(false);
  const thumbnailUrl = `${process.env.REACT_APP_BACKEND_URL}/api/videos/shorts/${video.id}/thumbnail`;

  return (
    <motion.div
      initial={{ opacity: 0, x: 30 }}
      animate={{ opacity: 1, x: 0 }}
      transition={{ duration: 0.5, delay: index * 0.06 }}
      data-testid={`featured-short-${video.id}`}
      className="shrink-0 w-[160px] md:w-[220px] group relative rounded-2xl overflow-hidden cursor-pointer"
      onMouseEnter={() => setHovered(true)}
      onMouseLeave={() => setHovered(false)}
      onClick={() => onClick(video)}
    >
      <div className="relative aspect-[9/16] overflow-hidden rounded-2xl bg-[#0F0F11]">
        <img
          src={thumbnailUrl}
          alt={video.title}
          className={`w-full h-full object-cover transition-transform duration-700 ${hovered ? "scale-110" : "scale-100"}`}
          loading="lazy"
          onError={(e) => { e.target.style.display = "none"; }}
        />
        <div className="absolute inset-0 bg-gradient-to-br from-[#1A1A1E] via-[#0F0F11] to-[#E63946]/10 -z-[1]" />

        <div className={`absolute inset-0 transition-all duration-500 ${
          hovered ? "bg-gradient-to-t from-black/80 via-black/20 to-transparent" : "bg-gradient-to-t from-black/50 via-transparent to-transparent"
        }`} />

        <div className={`absolute inset-0 flex items-center justify-center transition-all duration-300 ${hovered ? "opacity-100" : "opacity-0"}`}>
          <div className="w-10 h-10 md:w-12 md:h-12 rounded-full bg-[#E63946]/90 flex items-center justify-center shadow-lg shadow-[#E63946]/20">
            <Play size={16} className="text-white ml-0.5" fill="white" />
          </div>
        </div>

        {video.views && (
          <div className="absolute top-2 right-2 md:top-3 md:right-3 px-1.5 py-0.5 bg-black/60 backdrop-blur-sm text-white text-[9px] md:text-[10px] font-mono rounded-full flex items-center gap-1">
            <Eye size={8} /> {video.views}
          </div>
        )}

        <div className="absolute top-2 left-2 md:top-3 md:left-3">
          <svg width="14" height="14" viewBox="0 0 24 24" fill="white" opacity="0.6">
            <path d="M12 2.163c3.204 0 3.584.012 4.85.07 3.252.148 4.771 1.691 4.919 4.919.058 1.265.069 1.645.069 4.849 0 3.205-.012 3.584-.069 4.849-.149 3.225-1.664 4.771-4.919 4.919-1.266.058-1.644.07-4.85.07-3.204 0-3.584-.012-4.849-.07-3.26-.149-4.771-1.699-4.919-4.92-.058-1.265-.07-1.644-.07-4.849 0-3.204.013-3.583.07-4.849.149-3.227 1.664-4.771 4.919-4.919 1.266-.057 1.645-.069 4.849-.069zM12 0C8.741 0 8.333.014 7.053.072 2.695.272.273 2.69.073 7.052.014 8.333 0 8.741 0 12c0 3.259.014 3.668.072 4.948.2 4.358 2.618 6.78 6.98 6.98C8.333 23.986 8.741 24 12 24c3.259 0 3.668-.014 4.948-.072 4.354-.2 6.782-2.618 6.979-6.98.059-1.28.073-1.689.073-4.948 0-3.259-.014-3.667-.072-4.947-.196-4.354-2.617-6.78-6.979-6.98C15.668.014 15.259 0 12 0zm0 5.838a6.162 6.162 0 100 12.324 6.162 6.162 0 000-12.324zM12 16a4 4 0 110-8 4 4 0 010 8zm6.406-11.845a1.44 1.44 0 100 2.881 1.44 1.44 0 000-2.881z"/>
          </svg>
        </div>

        <div className="absolute bottom-0 left-0 right-0 p-2 md:p-3">
          <h4 className="font-heading text-[11px] md:text-xs font-semibold text-white leading-tight line-clamp-2 drop-shadow-lg">{video.title}</h4>
        </div>
      </div>

      {hovered && <div className="absolute -inset-[1px] rounded-2xl border border-[#E63946]/30 pointer-events-none" />}
    </motion.div>
  );
}

// ── Scroll Shelf for Shorts ────────────────────────────
function ShortsShelf({ shorts, onSelect }) {
  const scrollRef = useRef(null);
  const [canScrollLeft, setCanScrollLeft] = useState(false);
  const [canScrollRight, setCanScrollRight] = useState(true);

  const checkScroll = () => {
    const el = scrollRef.current;
    if (!el) return;
    setCanScrollLeft(el.scrollLeft > 10);
    setCanScrollRight(el.scrollLeft < el.scrollWidth - el.clientWidth - 10);
  };

  useEffect(() => {
    const el = scrollRef.current;
    if (el) { el.addEventListener("scroll", checkScroll); checkScroll(); return () => el.removeEventListener("scroll", checkScroll); }
  }, []);

  const scroll = (dir) => { const el = scrollRef.current; if (el) el.scrollBy({ left: dir * 220, behavior: "smooth" }); };

  return (
    <div className="relative">
      {canScrollLeft && (
        <button onClick={() => scroll(-1)} data-testid="shorts-scroll-left"
          className="absolute left-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-black/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-[#E63946]/80 transition-colors">
          <ChevronLeft size={16} />
        </button>
      )}
      {canScrollRight && (
        <button onClick={() => scroll(1)} data-testid="shorts-scroll-right"
          className="absolute right-0 top-1/2 -translate-y-1/2 z-10 w-8 h-8 md:w-10 md:h-10 rounded-full bg-black/70 backdrop-blur-sm border border-white/10 flex items-center justify-center text-white hover:bg-[#E63946]/80 transition-colors">
          <ChevronRight size={16} />
        </button>
      )}
      {canScrollLeft && <div className="absolute left-0 top-0 bottom-0 w-8 bg-gradient-to-r from-[#050505] to-transparent z-[5] pointer-events-none" />}
      {canScrollRight && <div className="absolute right-0 top-0 bottom-0 w-8 bg-gradient-to-l from-[#050505] to-transparent z-[5] pointer-events-none" />}

      <div ref={scrollRef} className="flex gap-3 md:gap-4 overflow-x-auto pb-4 px-1 snap-x snap-mandatory md:snap-none" style={{ scrollbarWidth: "none", msOverflowStyle: "none", WebkitOverflowScrolling: "touch" }}>
        {shorts.map((video, i) => (
          <div key={video.id} className="snap-start">
            <ShortVideoCard video={video} onClick={onSelect} index={i} />
          </div>
        ))}
      </div>
    </div>
  );
}

// ── Main Component ─────────────────────────────────────
export default function FeaturedContent() {
  const [activeTab, setActiveTab] = useState("longs");
  const [selectedVideo, setSelectedVideo] = useState(null);
  const [longs, setLongs] = useState([]);
  const [shorts, setShorts] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showAll, setShowAll] = useState(false);
  const MOBILE_LIMIT = 4;

  useEffect(() => {
    const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
    fetch(`${API}/videos`)
      .then((r) => r.json())
      .then((data) => { setLongs(data.longs || []); setShorts(data.shorts || []); setLoading(false); })
      .catch(() => setLoading(false));
  }, []);

  // Reset showAll when switching tabs
  useEffect(() => { setShowAll(false); }, [activeTab]);

  const visibleLongs = showAll ? longs : longs.slice(0, MOBILE_LIMIT);

  return (
    <section data-testid="featured-videos-section" className="py-16 md:py-32 bg-[#050505]">
      <div className="max-w-7xl mx-auto px-6 lg:px-8">
        {/* Header with toggle */}
        <div className="flex flex-col sm:flex-row sm:items-end sm:justify-between gap-4 sm:gap-6 mb-8 md:mb-12">
          <div>
            <span className="font-mono text-xs uppercase tracking-[0.2em] text-zinc-500">Featured Work</span>
            <h2 className="mt-2 font-heading text-2xl md:text-4xl tracking-tight font-bold text-white">Latest Videos</h2>
          </div>

          {/* Toggle - fixed for mobile */}
          <div data-testid="content-toggle" className="relative flex items-center bg-[#0F0F11] border border-white/10 rounded-full p-1 self-start sm:self-auto">
            <button onClick={() => setActiveTab("longs")} data-testid="toggle-longs"
              className={`relative z-10 px-5 md:px-6 py-2 md:py-2.5 text-sm font-semibold tracking-wide rounded-full transition-colors duration-200 ${activeTab === "longs" ? "text-white" : "text-zinc-500"}`}>
              Longs
            </button>
            <button onClick={() => setActiveTab("shorts")} data-testid="toggle-shorts"
              className={`relative z-10 px-5 md:px-6 py-2 md:py-2.5 text-sm font-semibold tracking-wide rounded-full transition-colors duration-200 ${activeTab === "shorts" ? "text-white" : "text-zinc-500"}`}>
              Shorts
            </button>
            {/* Sliding pill indicator */}
            <div
              className="absolute top-1 bottom-1 bg-[#E63946] rounded-full transition-all duration-300 ease-out"
              style={{
                width: "calc(50% - 4px)",
                left: activeTab === "longs" ? "4px" : "calc(50% + 0px)",
              }}
            />
          </div>
        </div>

        <AnimatePresence mode="wait">
          {activeTab === "longs" ? (
            <motion.div key="longs" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }} transition={{ duration: 0.3 }}>
              {/* Desktop: full grid. Mobile: limited with Show More */}
              <div className="hidden md:grid md:grid-cols-2 lg:grid-cols-3 gap-5">
                {longs.map((video, i) => (
                  <LongVideoCard key={video.id} video={video} onClick={setSelectedVideo} index={i} />
                ))}
              </div>

              {/* Mobile: limited grid */}
              <div className="grid grid-cols-2 gap-3 md:hidden">
                {visibleLongs.map((video, i) => (
                  <LongVideoCard key={video.id} video={video} onClick={setSelectedVideo} index={i} />
                ))}
              </div>

              {/* Show More button on mobile */}
              {!showAll && longs.length > MOBILE_LIMIT && (
                <div className="mt-6 text-center md:hidden">
                  <button
                    onClick={() => setShowAll(true)}
                    className="inline-flex items-center gap-2 px-6 py-3 border border-white/10 text-zinc-400 text-sm font-medium rounded-full hover:border-white/30 hover:text-white transition-all"
                  >
                    Show More ({longs.length - MOBILE_LIMIT} more)
                    <ChevronDown size={16} />
                  </button>
                </div>
              )}
            </motion.div>
          ) : (
            <motion.div key="shorts" initial={{ opacity: 0, y: 15 }} animate={{ opacity: 1, y: 0 }} exit={{ opacity: 0, y: -15 }} transition={{ duration: 0.3 }}>
              <ShortsShelf shorts={shorts} onSelect={setSelectedVideo} />
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      <AnimatePresence>
        {selectedVideo && (
          activeTab === "longs" ? (
            <VideoModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
          ) : (
            <ShortsModal video={selectedVideo} onClose={() => setSelectedVideo(null)} />
          )
        )}
      </AnimatePresence>
    </section>
  );
}
